import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The tooltip's rendered height, fed back into the placement hooks so an edge-placed bubble clamps
 * against its real half-height.
 *
 * Observed rather than measured against a dependency list: bubble content can arrive after the
 * first paint -- the PestPac "View Knock Status History" link resolves through a lookup (SH-1816)
 * -- and no prop or status the tooltip can watch changes when it does. A stale height leaves the
 * bubble overflowing the container by roughly the height of the content it grew by.
 *
 * Attaching through a ref callback (rather than a ref + effect) re-observes on its own when the
 * tooltip unmounts out of viewport and comes back, and avoids ordering it against the placement
 * hook that consumes the height.
 */
export function useMeasuredTooltipHeight(fallbackHeight: number): {
  height: number
  measureRef: (element: HTMLElement | null) => void
} {
  const [height, setHeight] = useState(fallbackHeight)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureRef = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!element) return

    const measure = () => {
      const measured = element.offsetHeight
      if (measured > 0) {
        setHeight((current) => (current === measured ? current : measured))
      }
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return

    observerRef.current = new ResizeObserver(measure)
    observerRef.current.observe(element)
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return { height, measureRef }
}
